import { CircleAlert } from "lucide-react"

import type { GuildEventRecord } from "@/lib/guild-events-api"

import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"

function isAnnouncementRecoveryNeeded(
  event: GuildEventRecord | undefined,
): boolean {
  return Boolean(
    event &&
    (event.status === "announcement_missing" ||
      event.syncStatus === "degraded" ||
      !event.discordMessageId ||
      Boolean(event.lastSyncError)),
  )
}

function getEditUnavailableReason(
  event: GuildEventRecord | undefined,
): string | null {
  if (!event || event.status === "scheduled") return null
  if (event.status === "started")
    return "Editing is unavailable after the event has started."
  if (event.status === "cancelled")
    return "Editing is unavailable for a cancelled event."
  return "Editing is unavailable after signups have closed or the announcement needs recovery."
}

export function EventDetailActions({
  event,
  onEdit,
  onCancel,
  onRepost,
  onSync,
  onDuplicate,
  onDelete,
  editPending,
  cancelPending,
  repostPending,
  syncPending,
  duplicatePending,
  deletePending,
}: {
  event: GuildEventRecord | undefined
  onEdit: () => void
  onCancel: () => void
  onRepost: () => void
  onSync: () => void
  onDuplicate: () => void
  onDelete: () => void
  editPending: boolean
  cancelPending: boolean
  repostPending: boolean
  syncPending: boolean
  duplicatePending: boolean
  deletePending: boolean
}) {
  const recoveryNeeded = isAnnouncementRecoveryNeeded(event)
  const editUnavailableReason = getEditUnavailableReason(event)
  const cancelled = event?.status === "cancelled"

  return (
    <Card>
      <CardContent className="grid gap-5 pt-5">
        <div>
          <h2 className="text-sm font-semibold">Admin actions</h2>
          <p className="text-sm text-muted-foreground">
            Availability is shown here; server-side event rules remain
            authoritative.
          </p>
        </div>

        <section className="grid gap-2" aria-labelledby="manage-actions">
          <h3 id="manage-actions" className="text-sm font-semibold">
            Manage
          </h3>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="default"
              onClick={onEdit}
              disabled={editPending || Boolean(editUnavailableReason)}
              aria-describedby={
                editUnavailableReason ? "edit-unavailable-reason" : undefined
              }
            >
              {editPending ? "Saving..." : "Edit Event"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onDuplicate}
              disabled={duplicatePending || cancelled}
              aria-describedby={
                cancelled ? "duplicate-unavailable-reason" : undefined
              }
            >
              {duplicatePending ? "Duplicating..." : "Duplicate"}
            </Button>
          </div>
          {editUnavailableReason ? (
            <p
              id="edit-unavailable-reason"
              className="text-xs text-muted-foreground"
            >
              {editUnavailableReason}
            </p>
          ) : null}
          {cancelled ? (
            <p
              id="duplicate-unavailable-reason"
              className="text-xs text-muted-foreground"
            >
              Duplicating is unavailable for a cancelled event.
            </p>
          ) : null}
        </section>

        <section
          className="grid gap-2"
          aria-labelledby="announcement-recovery-actions"
        >
          <div>
            <h3
              id="announcement-recovery-actions"
              className="text-sm font-semibold"
            >
              Announcement recovery
            </h3>
            <p className="text-sm text-muted-foreground">
              Restore a missing or degraded Discord announcement.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onSync}
              disabled={syncPending || !recoveryNeeded}
              aria-describedby={
                !recoveryNeeded ? "sync-unavailable-reason" : undefined
              }
            >
              {syncPending ? "Syncing..." : "Sync Announcement"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onRepost}
              disabled={repostPending}
            >
              {repostPending ? "Reposting..." : "Repost Announcement"}
            </Button>
          </div>
          {!recoveryNeeded ? (
            <p
              id="sync-unavailable-reason"
              className="text-xs text-muted-foreground"
            >
              Sync is available only when an announcement is missing or
              degraded. Repost can publish a new announcement.
            </p>
          ) : null}
          {event?.lastSyncError ? (
            <div
              className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
              role="alert"
            >
              <CircleAlert className="mt-0.5 size-4 shrink-0" />
              <div className="grid gap-1">
                <span className="font-medium">
                  Last recovery attempt failed
                </span>
                <span className="break-words">{event.lastSyncError}</span>
                <span className="text-xs">
                  Use Sync Announcement or Repost Announcement to recover
                  delivery.
                </span>
              </div>
            </div>
          ) : null}
        </section>

        <section
          className="grid gap-2 border-t border-border pt-4"
          aria-labelledby="danger-zone-actions"
        >
          <h3
            id="danger-zone-actions"
            className="text-sm font-semibold text-destructive"
          >
            Danger zone
          </h3>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="destructive"
              onClick={onCancel}
              disabled={cancelPending || cancelled}
              aria-describedby={
                cancelled ? "cancel-unavailable-reason" : undefined
              }
            >
              {cancelPending ? "Cancelling..." : "Cancel Event"}
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={onDelete}
              disabled={deletePending}
            >
              {deletePending ? "Deleting..." : "Delete Event"}
            </Button>
          </div>
          {cancelled ? (
            <p
              id="cancel-unavailable-reason"
              className="text-xs text-muted-foreground"
            >
              This event has already been cancelled.
            </p>
          ) : null}
        </section>
      </CardContent>
    </Card>
  )
}
